import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Stack,
  CircularProgress,
  Alert,
  Divider,
  MenuItem,
  Collapse,
  Link,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useNotify, useRefresh, useGetOne } from 'react-admin';
import { Funeral, Partner, Client } from '../types';
import { getToken } from '../authentication/authProvider';
import {
  FAMILY_SEND_WINDOW,
  describeSendTime,
  isFamilySendDay,
  isWithinSendWindow,
  nextSendTime,
} from '../lib/familySendWindow';
import { MAX_DELIVERY_INSTRUCTIONS, familyInstructionsFrom } from './deliveryInstructions';

/** Courrier Plus service levels (Groupe V codes). */
const SERVICE_OPTIONS = [
  { code: '062', label: 'Régulier (2-3h)' },
  { code: '064', label: 'Même jour' },
  { code: '061', label: 'Urgent' },
  { code: '009', label: 'Direct' },
  { code: '066', label: 'Lendemain' },
  { code: '070', label: 'Extérieur (hors zone)' },
];

interface PriceEstimate {
  base: number;
  weightSurcharge: number;
  fuelSurcharge: number;
  subtotal: number;
  gst: number;
  qst: number;
  total: number;
}

interface PickupOverride {
  name: string;
  line1: string;
  city: string;
  province: string;
  postalCode: string;
  phone: string;
}

interface DeliveryEdit {
  name: string;
  line1: string;
  unit: string;
  city: string;
  province: string;
  postalCode: string;
  phone: string;
}

interface CourrierPlusModalProps {
  open: boolean;
  onClose: () => void;
  funeral: Funeral;
}

const apiBase = () => {
  const apiUrl = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
  return apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
};

/**
 * Why the day-of text will not go right away, or null when it will. It is only
 * ever about a delivery today, so a text that can't go today doesn't go at all.
 */
const familyTextTiming = (now: Date = new Date()): string | null => {
  if (isWithinSendWindow(now)) return null;
  if (!isFamilySendDay(now)) {
    return 'No automatic texts on weekends, so the family will not be texted for a delivery today — call them if they need to know.';
  }
  const sendAt = nextSendTime(now);
  const torontoDay = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  if (torontoDay(sendAt) === torontoDay(now)) {
    return `Will send at ${describeSendTime(sendAt, now)}, not now.`;
  }
  return `It is after ${FAMILY_SEND_WINDOW.endHour - 12} pm, so no text will be sent for a delivery today.`;
};

export const CourrierPlusModal: React.FC<CourrierPlusModalProps> = ({
  open,
  onClose,
  funeral,
}) => {
  const notify = useNotify();
  const refresh = useRefresh();

  const [serviceCode, setServiceCode] = useState('062');
  const [estimate, setEstimate] = useState<PriceEstimate | null>(null);
  const [pricing, setPricing] = useState(false);
  const [booking, setBooking] = useState(false);
  // On by default: the family is waiting on this driver, so telling them is the
  // normal case and silence is the deliberate one.
  const [notifyFamily, setNotifyFamily] = useState(true);

  // Driver note. Defaults to a bilingual "ask for <deceased>" once we've loaded
  // the deceased's name; `noteEdited` stops us clobbering the coordinator's edits.
  const [note, setNote] = useState('');
  const [noteEdited, setNoteEdited] = useState(false);

  // The family's own words for the driver (buzzer, door…), from the "Family:"
  // line they left on the delivery page. Printed above the note on the slip;
  // an edit here is what the driver gets, and is saved back to the case on
  // booking.
  const familyInstructions = familyInstructionsFrom(funeral.delivery_note);
  const [instructions, setInstructions] = useState(familyInstructions);
  useEffect(() => {
    if (open) setInstructions(familyInstructions);
  }, [open, familyInstructions]);

  // Manual pickup override (defaults to the case's crematorium when left blank).
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [pickup, setPickup] = useState<PickupOverride>({
    name: '',
    line1: '',
    city: '',
    province: 'QC',
    postalCode: '',
    phone: '',
  });

  const delivery = funeral.delivery_address;

  // Editable delivery details (name, address, unit, phone). Pre-filled from the
  // case; when the panel is open and complete, it's sent as a deliveryOverride.
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [deliveryEdit, setDeliveryEdit] = useState<DeliveryEdit>(() => ({
    name: delivery?.recipient_name || '',
    line1: [delivery?.civic_number, delivery?.address].filter(Boolean).join(' '),
    unit: delivery?.apartment || '',
    city: delivery?.city || '',
    province: delivery?.province || 'QC',
    postalCode: delivery?.postal_code || '',
    phone: delivery?.recipient_phone || '',
  }));

  const deliveryOverride = () => {
    // Send only when the coordinator opened the editor and it has the essentials.
    if (deliveryOpen && deliveryEdit.line1 && deliveryEdit.city && deliveryEdit.postalCode) {
      return deliveryEdit;
    }
    return undefined;
  };

  // The case's crematorium is the default pickup; show its name + address so the
  // coordinator can confirm what the courier will pick up from.
  const { data: crematorium } = useGetOne<Partner>(
    'partners',
    { id: funeral.crematorium_partner_id as string },
    { enabled: open && !!funeral.crematorium_partner_id },
  );

  // A pickup's courier leg ends at the pickup location — our office, or a
  // Southwest location — not at the family, who may have no address on the case
  // at all. Load that partner and seed the delivery editor with it; an open,
  // complete editor is what travels as the deliveryOverride, so nothing about
  // how the booking is sent has to change.
  const isPickupTransfer =
    !funeral.courier_call_number &&
    !!funeral.pickup_location_id &&
    (funeral.delivery_method === 'office_pickup' ||
      funeral.delivery_method === 'crematorium_pickup' ||
      funeral.delivery_method === 'pickup');

  const { data: pickupLocation } = useGetOne<Partner>(
    'partners',
    { id: funeral.pickup_location_id as string },
    { enabled: open && isPickupTransfer },
  );

  // Seed once per opening; `deliverySeeded` stops us clobbering the
  // coordinator's edits the way `noteEdited` does for the note.
  const [deliverySeeded, setDeliverySeeded] = useState(false);
  useEffect(() => {
    if (!open || !pickupLocation || deliverySeeded) return;
    setDeliveryEdit({
      name: pickupLocation.name || '',
      line1: pickupLocation.address || '',
      unit: '',
      city: pickupLocation.city || '',
      province: pickupLocation.province || 'QC',
      postalCode: pickupLocation.postal_code || '',
      phone: pickupLocation.phone || '',
    });
    setDeliveryOpen(true);
    setDeliverySeeded(true);
  }, [open, pickupLocation, deliverySeeded]);

  // Deceased name → the default driver note.
  const { data: deceased } = useGetOne<Client>(
    'clients',
    { id: funeral.deceased_client_id as string },
    { enabled: open && !!funeral.deceased_client_id },
  );
  const deceasedName = [deceased?.first_name, deceased?.last_name].filter(Boolean).join(' ').trim();
  const win = funeral.delivery_window?.trim();
  const defaultNote = deceasedName
    ? win
      ? `Veuillez livrer entre ${win}. Demander ${deceasedName}. Merci.\nPlease deliver between ${win}. Ask for ${deceasedName}. Thank you.`
      : `Veuillez demander ${deceasedName}, merci.\nPlease ask for ${deceasedName}, thank you.`
    : '';

  // Seed the note once the name loads, unless the coordinator has already edited it.
  useEffect(() => {
    if (open && defaultNote && !noteEdited) {
      setNote(defaultNote);
    }
  }, [open, defaultNote, noteEdited]);

  // "Recipient (Deceased)" — e.g. "Bram Paperman (Marsha Chuk)". Any existing
  // trailing parenthetical on the recipient (e.g. "(Cleo)") is dropped first.
  const recipientLabel = () => {
    const base = (delivery?.recipient_name || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
    if (base && deceasedName) return `${base} (${deceasedName})`;
    return base || deceasedName || '';
  };

  const formatCrematorium = () => {
    if (!crematorium) {
      return funeral.crematorium_partner_id
        ? 'Loading crematorium…'
        : 'No crematorium set on this case';
    }
    const rest = [crematorium.address, crematorium.city, crematorium.province, crematorium.postal_code]
      .filter(Boolean)
      .join(', ');
    return rest || 'Crematorium has no address on file';
  };

  const pickupOverride = () => {
    // Only send an override when the coordinator actually filled the address in.
    if (overrideOpen && pickup.line1 && pickup.city && pickup.postalCode) {
      return pickup;
    }
    return undefined;
  };

  const post = async (path: string) => {
    const token = await getToken();
    const response = await fetch(`${apiBase()}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        serviceCode,
        pickupOverride: pickupOverride(),
        deliveryOverride: deliveryOverride(),
        specialInstructions: note.trim() || undefined,
        // Only a delivery to the family has a door to ring at; a transfer to
        // our office leaves the case's note alone.
        deliveryInstructions: isPickupTransfer ? undefined : instructions.trim(),
        notifyFamily,
      }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Request failed');
    }
    return response.json();
  };

  const handleEstimate = async () => {
    try {
      setPricing(true);
      setEstimate(await post(`/courrier-plus/funeral/${funeral.id}/estimate`));
    } catch (error) {
      notify(`Could not price courier: ${error instanceof Error ? error.message : 'error'}`, {
        type: 'error',
      });
    } finally {
      setPricing(false);
    }
  };

  const handleBook = async () => {
    try {
      setBooking(true);
      const result = await post(`/courrier-plus/funeral/${funeral.id}/book`);
      notify(`Courier booked — call #${result.callNumber}`, { type: 'success' });
      void refresh();
      resetAndClose();
    } catch (error) {
      notify(`Could not book courier: ${error instanceof Error ? error.message : 'error'}`, {
        type: 'error',
      });
    } finally {
      setBooking(false);
    }
  };

  const resetAndClose = () => {
    setEstimate(null);
    setServiceCode('062');
    setNotifyFamily(true);
    setOverrideOpen(false);
    setDeliveryOpen(false);
    setDeliverySeeded(false);
    setNote('');
    setNoteEdited(false);
    onClose();
  };

  const formatDelivery = () => {
    if (!delivery) return 'No delivery address set';
    const street = [delivery.civic_number, delivery.address].filter(Boolean).join(' ');
    return [street, delivery.apartment, delivery.city, delivery.province, delivery.postal_code]
      .filter(Boolean)
      .join(', ');
  };

  const money = (n: number) => `$${(n ?? 0).toFixed(2)}`;

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="sm" fullWidth onClick={(e) => e.stopPropagation()}>
      <DialogTitle>Schedule Courier (Courrier Plus)</DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Pickup */}
          <Box>
            <Typography variant="subtitle2" fontWeight="600" gutterBottom>
              Pickup
            </Typography>
            {overrideOpen && pickup.line1 ? (
              <Typography variant="body2" color="text.secondary">
                {`${pickup.name || 'Manual address'} — ${pickup.line1}, ${pickup.city} ${pickup.postalCode}`}
              </Typography>
            ) : (
              <>
                {crematorium?.name && (
                  <Typography variant="body2" fontWeight="600">{crematorium.name}</Typography>
                )}
                <Typography variant="body2" color="text.secondary">{formatCrematorium()}</Typography>
              </>
            )}
            <Link
              component="button"
              variant="caption"
              onClick={() => setOverrideOpen((v) => !v)}
              sx={{ mt: 0.5 }}
            >
              {overrideOpen ? 'Use crematorium address' : 'Enter a manual pickup address'}
            </Link>
            <Collapse in={overrideOpen}>
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                <TextField label="Name" size="small" value={pickup.name}
                  onChange={(e) => setPickup({ ...pickup, name: e.target.value })} />
                <TextField label="Address" size="small" value={pickup.line1}
                  onChange={(e) => setPickup({ ...pickup, line1: e.target.value })} />
                <Stack direction="row" spacing={1.5}>
                  <TextField label="City" size="small" fullWidth value={pickup.city}
                    onChange={(e) => setPickup({ ...pickup, city: e.target.value })} />
                  <TextField label="Province" size="small" sx={{ width: 110 }} value={pickup.province}
                    onChange={(e) => setPickup({ ...pickup, province: e.target.value })} />
                  <TextField label="Postal code" size="small" sx={{ width: 140 }} value={pickup.postalCode}
                    onChange={(e) => setPickup({ ...pickup, postalCode: e.target.value })} />
                </Stack>
                <TextField label="Phone" size="small" value={pickup.phone}
                  onChange={(e) => setPickup({ ...pickup, phone: e.target.value })} />
              </Stack>
            </Collapse>
          </Box>

          <Divider />

          {/* Delivery */}
          <Box>
            <Typography variant="subtitle2" fontWeight="600" gutterBottom>
              Deliver to
            </Typography>
            {deliveryOpen ? (
              <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                <TextField label="Name" size="small" value={deliveryEdit.name}
                  onChange={(e) => setDeliveryEdit({ ...deliveryEdit, name: e.target.value })} />
                <TextField label="Address (civic number + street)" size="small" value={deliveryEdit.line1}
                  onChange={(e) => setDeliveryEdit({ ...deliveryEdit, line1: e.target.value })} />
                <Stack direction="row" spacing={1.5}>
                  <TextField label="Unit / Apt" size="small" sx={{ width: 130 }} value={deliveryEdit.unit}
                    onChange={(e) => setDeliveryEdit({ ...deliveryEdit, unit: e.target.value })} />
                  <TextField label="City" size="small" fullWidth value={deliveryEdit.city}
                    onChange={(e) => setDeliveryEdit({ ...deliveryEdit, city: e.target.value })} />
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  <TextField label="Province" size="small" sx={{ width: 110 }} value={deliveryEdit.province}
                    onChange={(e) => setDeliveryEdit({ ...deliveryEdit, province: e.target.value })} />
                  <TextField label="Postal code" size="small" sx={{ width: 140 }} value={deliveryEdit.postalCode}
                    onChange={(e) => setDeliveryEdit({ ...deliveryEdit, postalCode: e.target.value })} />
                  <TextField label="Phone" size="small" fullWidth value={deliveryEdit.phone}
                    onChange={(e) => setDeliveryEdit({ ...deliveryEdit, phone: e.target.value })} />
                </Stack>
              </Stack>
            ) : (
              <>
                {recipientLabel() && (
                  <Typography variant="body2" fontWeight="600">{recipientLabel()}</Typography>
                )}
                <Typography variant="body2" color="text.secondary">{formatDelivery()}</Typography>
                <Typography
                  variant="body2"
                  color={delivery?.recipient_phone ? 'text.secondary' : 'warning.main'}
                >
                  {delivery?.recipient_phone
                    ? `☎ ${delivery.recipient_phone}`
                    : 'No delivery phone on file'}
                </Typography>
              </>
            )}
            <Link
              component="button"
              variant="caption"
              onClick={() => {
                setDeliveryOpen((v) => !v);
                setEstimate(null); // re-price if the address changed
              }}
              sx={{ mt: 0.5, display: 'block' }}
            >
              {deliveryOpen ? 'Done editing' : 'Edit delivery details'}
            </Link>
          </Box>

          <Divider />

          {/* Service */}
          <TextField
            select
            label="Service level"
            size="small"
            value={serviceCode}
            onChange={(e) => {
              setServiceCode(e.target.value);
              setEstimate(null); // re-price on change
            }}
          >
            {SERVICE_OPTIONS.map((s) => (
              <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>
            ))}
          </TextField>

          {/* The family's delivery instructions — only a delivery has a door to ring at */}
          {!isPickupTransfer && (
            <TextField
              id="courier-delivery-instructions"
              label="Delivery instructions (from family)"
              size="small"
              multiline
              minRows={1}
              value={instructions}
              onChange={(e) => {
                setInstructions(e.target.value.slice(0, MAX_DELIVERY_INSTRUCTIONS));
                setEstimate(null);
              }}
              placeholder="Buzzer number or anything else the driver needs to know"
              helperText={
                familyInstructions
                  ? 'Left by the family on the delivery page. Printed on the slip under the apartment; edits are saved to the case.'
                  : 'Printed on the slip under the apartment; saved to the case on booking.'
              }
            />
          )}

          {/* Driver note — defaults to a bilingual "ask for <deceased>" message */}
          <TextField
            label="Note for driver"
            size="small"
            multiline
            minRows={2}
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              setNoteEdited(true);
            }}
            helperText="Sent to the courier as special instructions."
          />

          {/* Price breakdown */}
          {estimate && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <PriceRow label="Base" value={money(estimate.base)} />
              {estimate.weightSurcharge > 0 && <PriceRow label="Weight" value={money(estimate.weightSurcharge)} />}
              {estimate.fuelSurcharge > 0 && <PriceRow label="Fuel" value={money(estimate.fuelSurcharge)} />}
              <PriceRow label="GST" value={money(estimate.gst)} />
              <PriceRow label="QST" value={money(estimate.qst)} />
              <Divider sx={{ my: 1 }} />
              <PriceRow label="Total" value={money(estimate.total)} bold />
            </Box>
          )}

          <Alert severity="info" variant="outlined">
            Booking dispatches a real driver to pick up from the crematorium.
          </Alert>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={notifyFamily}
                  onChange={(e) => setNotifyFamily(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  Text the family that the driver is on the way
                </Typography>
              }
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', ml: 4, mt: -0.5 }}
            >
              Only sends when the delivery is today. Uncheck to book without telling
              them — a redelivery, or a family already spoken to.
            </Typography>
            {notifyFamily && familyTextTiming() && (
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ display: 'block', ml: 4, mt: 0.5 }}
              >
                {familyTextTiming()}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={resetAndClose} disabled={pricing || booking}>Cancel</Button>
        {!estimate ? (
          <Button variant="outlined" onClick={handleEstimate} disabled={pricing}>
            {pricing ? <><CircularProgress size={18} sx={{ mr: 1 }} />Pricing…</> : 'Get price'}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleBook} disabled={booking}>
            {booking ? <><CircularProgress size={18} sx={{ mr: 1 }} />Booking…</> : `Book courier — ${money(estimate.total)}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const PriceRow: React.FC<{ label: string; value: string; bold?: boolean }> = ({ label, value, bold }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography variant="body2" fontWeight={bold ? 700 : 400}>{label}</Typography>
    <Typography variant="body2" fontWeight={bold ? 700 : 400}>{value}</Typography>
  </Stack>
);
